import { Form, Switch } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import {
  Calendar,
  Check,
  Mail,
  Monitor,
  Tool,
  User,
  Users,
} from 'react-feather';
import { PackTemplate } from '../../../interfaces/AssetPacks';
import ListComponent from '../../../utils/ListComponent';
import CurrencyFormatter from '../../../utils/text/CurrencyFormatter';
import ImagePlaceholder from '../../images/ImagePlaceholder';
import Typography from '../../typography/Typography';

interface props {
  data: any;
}

const HardwareOnboardingComfirmation: FC<props> = ({ data }) => {
  const [total, setTotal] = useState(0);
  const [oneTimeTotal, setOneTimeTotal] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const calculateTotal = () => {
    const result = data.selectedPacks.reduce(
      (
        acc: { total: number; monthly: number; oneTime: number },
        item: PackTemplate
      ) => {
        const rawPrice = item?.Price ?? '0';
        const number = parseFloat(String(rawPrice).replace(',', '.'));
        const price = isNaN(number) ? 0 : number;

        acc.total += price;

        if (item.type === 'LICENSE') {
          acc.monthly += price;
        } else {
          acc.oneTime += price;
        }

        return acc;
      },
      { total: 0, monthly: 0, oneTime: 0 }
    );

    setTotal(result.total);
    setOneTimeTotal(result.oneTime);
    setMonthlyTotal(result.monthly);
  };

  useEffect(() => {
    calculateTotal();
  }, [data.assetPackSelection]);

  return (
    <div>
      <div className="mb-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <User size={14} />
            <Typography tag="p" type="label">
              {data.name} {data.surname}
            </Typography>
          </div>

          <div className="flex items-center gap-2">
            <Mail size={14} />
            <Typography tag="p" type="label">
              {data.email}
            </Typography>
          </div>

          <div className="flex items-center gap-2">
            <Users size={14} />
            <Typography tag="p" type="label">
              {data.groupName}
            </Typography>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <Typography tag="span" type="subtitle">
          Hardware & licenties
        </Typography>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ListComponent
          data={data.selectedPacks}
          renderItem={(item) => (
            <div className="border mb-4 p-4 rounded">
              {/* Header Row */}
              <div className="border-b py-2 mb-2 flex justify-between items-center">
                <Typography tag="h4" type="bold">
                  {item.PackType}: {item.PackName}
                </Typography>

                <Typography tag="h4" type="bold" addClass="text-right">
                  € {item.Price}
                </Typography>
              </div>
              {item.PackDetails.products && (
                <ListComponent
                  data={item.PackDetails.products}
                  renderItem={(detail) => (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                        {detail.image ? (
                          <img
                            src={detail.image}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <ImagePlaceholder size={10} iconSize={6} />
                        )}
                      </div>
                      <div>
                        <Typography tag="p" type="label">
                          {detail.name}
                        </Typography>
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
          )}
        />
      </div>
      <div>
        <Typography tag="span" type="subtitle">
          Estimate
        </Typography>
      </div>
      <div>
        <Typography tag="span" type="small">
          De prijzen kunnen een lichte afwijking hebben
        </Typography>

        <ul className="w-full divide-y divide-gray-200 dark:divide-gray-700 mt-4">
          <li className="pb-3 sm:pb-4">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div>
                <Monitor className="text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <Typography tag="p" type="label">
                  Eenmalige kost
                </Typography>
                <Typography tag="p" type="faded">
                  Hardware
                </Typography>
              </div>
              <Typography tag="p" type="semibold">
                <CurrencyFormatter price={oneTimeTotal} tag="excl. btw" />
              </Typography>
            </div>
          </li>
          <li className="pb-3 sm:pb-4">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div>
                <Calendar className="text-blue-300" />
              </div>
              <div className="flex-1 min-w-0 mt-2">
                <Typography tag="p" type="label">
                  Maandelijkse kost
                </Typography>
                <Typography tag="p" type="faded">
                  Licenties
                </Typography>
              </div>
              <Typography tag="p" type="semibold">
                <CurrencyFormatter price={monthlyTotal} tag="excl. btw" />
              </Typography>
            </div>
          </li>
          {data.cleanInstall && data.unassignedHardware?.length > 0 ? (
            <li className="pb-3 sm:pb-4">
              <div className="flex items-center space-x-4 rtl:space-x-reverse">
                <div>
                  <Tool className="text-blue-300" />
                </div>
                <div className="flex-1 min-w-0 mt-2">
                  <Typography tag="p" type="label">
                    Herinstalleren nieuwe gebruiker
                  </Typography>
                  <Typography tag="p" type="faded">
                    Prijs in regie
                  </Typography>
                </div>
                <Typography tag="p" type="bold">
                  /
                </Typography>
              </div>
            </li>
          ) : (
            ''
          )}
          <li className="pb-3 sm:pb-4">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div>
                <Check className="text-blue-300" />
              </div>
              <div className="flex-1 min-w-0 mt-2">
                <Typography tag="p" type="label">
                  Totaal
                </Typography>
                <Typography tag="p" type="faded">
                  Geschatte kostprijs
                </Typography>
              </div>
              <Typography tag="p" type="bold">
                <CurrencyFormatter price={total} tag="excl. btw" />
              </Typography>
            </div>
          </li>
        </ul>
        <div className="flex items-center mt-4">
          <div className="flex items-center space-x-3">
            <Form.Item
              name="agree"
              valuePropName="checked"
              className="!mb-0 flex items-center !h-auto"
              rules={[
                {
                  required: true,
                  message: 'Je moet akkoord gaan voordat je verder kunt.',
                },
              ]}
            >
              <Switch className="antd-switch-fix align-middle" />
            </Form.Item>
            <Typography
              tag="label"
              type="label"
              addClass="leading-tight align-middle"
            >
              Ik ga ermee akkoord dat CMC de producten die nodig zijn besteld
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
export default HardwareOnboardingComfirmation;
